import React, { useState, useEffect, useCallback } from 'react';
import './Gallery.css';
import Lightbox from './Lightbox';

const Gallery: React.FC = () => {
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [currentPage, setCurrentPage] = useState(0);
  const [isPaused, setIsPaused] = useState(false);

  // 44 resim
  const galleryImages = Array.from({ length: 44 }, (_, i) => ({
    src: `/images/gallery/galeri-${i + 1}.jpg`,
    alt: `Cennetin Çocukları Anaokulu etkinlik ve sınıf fotoğrafı ${i + 1}`,
  }));

  const imagesPerPage = 8;
  const totalPages = Math.ceil(galleryImages.length / imagesPerPage);

  const getCurrentPageImages = () => {
    const start = currentPage * imagesPerPage;
    return galleryImages.slice(start, start + imagesPerPage);
  };

  const goToNextPage = useCallback(() => {
    setCurrentPage((prev) => (prev + 1) % totalPages);
  }, [totalPages]);

  const goToPrevPage = () => {
    setCurrentPage((prev) => (prev - 1 + totalPages) % totalPages);
  };

  const openLightbox = (pageIndex: number) => {
    const actualIndex = currentPage * imagesPerPage + pageIndex;
    setCurrentIndex(actualIndex);
    setLightboxOpen(true);
    setIsPaused(true);
  };

  const closeLightbox = () => {
    setLightboxOpen(false);
    setIsPaused(false);
  };

  const goToPrev = () => {
    setCurrentIndex((prev) => (prev > 0 ? prev - 1 : galleryImages.length - 1));
  };

  const goToNext = () => {
    setCurrentIndex((prev) => (prev < galleryImages.length - 1 ? prev + 1 : 0));
  };

  // Auto-play slider
  useEffect(() => {
    if (isPaused || lightboxOpen) return;

    const interval = setInterval(() => {
      goToNextPage();
    }, 5000);

    return () => clearInterval(interval);
  }, [isPaused, lightboxOpen, goToNextPage]);

  return (
    <section id="gallery" className="gallery-section">
      {/* Top Wave */}
      <div className="gallery-wave-top">
        <svg viewBox="0 0 1440 100" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M0,50 C120,20 240,80 360,50 C480,20 600,80 720,50 C840,20 960,80 1080,50 C1200,20 1320,80 1440,50 L1440,0 L0,0 Z"
                fill="#7B3FF2" />
        </svg>
      </div>

      <div className="gallery-container">
        {/* Section Title */}
        <h2 className="gallery-title">Galerimiz</h2>

        {/* Gallery Slider */}
        <div
          className="gallery-slider-wrapper"
          onMouseEnter={() => setIsPaused(true)}
          onMouseLeave={() => setIsPaused(false)}
        >
          {/* Navigation Arrows */}
          <button className="gallery-nav-arrow gallery-nav-prev" onClick={goToPrevPage}>
            <i className="fas fa-chevron-left"></i>
          </button>

          {/* Gallery Grid */}
          <div className="gallery-grid">
            {getCurrentPageImages().map((image, index) => (
              <div
                key={`${currentPage}-${index}`}
                className="gallery-item"
                onClick={() => openLightbox(index)}
              >
                <div className="gallery-image">
                  <img src={image.src} alt={image.alt} />
                  <div className="gallery-overlay">
                    <i className="fas fa-search-plus"></i>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <button className="gallery-nav-arrow gallery-nav-next" onClick={goToNextPage}>
            <i className="fas fa-chevron-right"></i>
          </button>
        </div>

        {/* Page Dots */}
        <div className="gallery-dots">
          {Array.from({ length: totalPages }, (_, i) => (
            <button
              key={i}
              className={`gallery-dot ${currentPage === i ? 'active' : ''}`}
              onClick={() => setCurrentPage(i)}
            />
          ))}
        </div>

        {/* Page Counter */}
        <p className="gallery-counter">{currentPage + 1} / {totalPages}</p>
      </div>

      {/* Lightbox */}
      <Lightbox
        isOpen={lightboxOpen}
        imageSrc={galleryImages[currentIndex]?.src || ''}
        imageAlt={galleryImages[currentIndex]?.alt || ''}
        onClose={closeLightbox}
        onPrev={goToPrev}
        onNext={goToNext}
        hasPrev={true}
        hasNext={true}
      />
    </section>
  );
};

export default Gallery;
